import { LotSpecies } from '../../lib/types/lots';

export function getSpeciesName(species: LotSpecies | null | undefined): string {
  return species?.name ?? '—';
}

export function formatNumberFr(
  value: number | null | undefined,
  decimals = 2,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return value.toFixed(decimals).replace('.', ',');
}

export function formatTonnage(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `${formatNumberFr(value)} t`;
}

export function formatAreaHa(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `${formatNumberFr(value)} ha`;
}

export function formatYield(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `${formatNumberFr(value)} t/ha`;
}

export function formatSignedDelta(
  value: number | null | undefined,
  unit: string,
): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumberFr(value)} ${unit}`;
}

export function tonnageDelta(
  real: number | null | undefined,
  oleoze: number | null | undefined,
): { absolute: number | null; percent: number | null } {
  if (
    real == null ||
    oleoze == null ||
    Number.isNaN(real) ||
    Number.isNaN(oleoze)
  ) {
    return { absolute: null, percent: null };
  }
  const absolute = real - oleoze;
  const percent = oleoze !== 0 ? (absolute / oleoze) * 100 : null;
  return { absolute, percent };
}

export function getTonnageDeltaValue(lot: {
  real_tonnage: number | null;
  oleoze_tonnage: number | null;
  tonnage_delta?: number | null;
}): number | null {
  if (lot.tonnage_delta != null && !Number.isNaN(lot.tonnage_delta)) {
    return lot.tonnage_delta;
  }
  return tonnageDelta(lot.real_tonnage, lot.oleoze_tonnage).absolute;
}

export function getTonnageDeltaPercentValue(lot: {
  real_tonnage: number | null;
  oleoze_tonnage: number | null;
  tonnage_delta_percent?: number | null;
}): number | null {
  if (
    lot.tonnage_delta_percent !== undefined &&
    lot.tonnage_delta_percent !== null
  ) {
    return lot.tonnage_delta_percent;
  }
  return tonnageDelta(lot.real_tonnage, lot.oleoze_tonnage).percent;
}

export function getAreaDeltaValue(lot: {
  calculated_area: number | null;
  theoretical_area: number | null;
  area_delta?: number | null;
}): number | null {
  if (lot.area_delta !== undefined && lot.area_delta !== null) {
    return lot.area_delta;
  }
  return areaDelta(lot.calculated_area, lot.theoretical_area);
}

export function getYieldDeltaValue(lot: {
  recalculated_yield: number | null;
  real_yield: number | null;
  yield_delta?: number | null;
}): number | null {
  if (lot.yield_delta !== undefined && lot.yield_delta !== null) {
    return lot.yield_delta;
  }
  return yieldDelta(lot.recalculated_yield, lot.real_yield);
}

export function areaDelta(
  calculated: number | null | undefined,
  theoretical: number | null | undefined,
): number | null {
  if (
    calculated == null ||
    theoretical == null ||
    Number.isNaN(calculated) ||
    Number.isNaN(theoretical)
  ) {
    return null;
  }
  return calculated - theoretical;
}

export function yieldDelta(
  recalculated: number | null | undefined,
  real: number | null | undefined,
): number | null {
  if (
    recalculated == null ||
    real == null ||
    Number.isNaN(recalculated) ||
    Number.isNaN(real)
  ) {
    return null;
  }
  return recalculated - real;
}

export function isLargeDeltaPercent(percent: number | null, threshold = 10) {
  return percent !== null && Math.abs(percent) >= threshold;
}
